import { Modal, notification } from "antd";
import cuid from "cuid";
import { defaults, filter, get, keyBy, last, map, sortBy } from "lodash";
import { useEffect, useState } from "react";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import EmailGreen from "shared/components/SVG/LMF/EmailGreen";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import { SEND_B2C_QUOTE } from "utils/api/graphql/mutations/quotes";
import { SEND_PERSONALIZED_QUOTE } from "utils/api/graphql/mutations/webhook";
import { GENERAL_COST } from "utils/api/graphql/queries/general-cost";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import { v4 } from "uuid";
import { filterByConditions } from "../../../..";
import Template from "../Template";
import View from "./View";

const AdherentDetails = ({
  project,
  tracer,
  currentStatus,
  onNext,
  processId,
  onBack,
}) => {
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [sendPersonalizedQuote] = useMutation(SEND_PERSONALIZED_QUOTE);

  const [addLink] = useMutation(CREATE_LINK);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [successModalVisible, setSuccessModalVisible] = useState(false);

  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem("subscriptionId");

  const [getSubscription, { data }] = useLazyQuery(
    graphql.queries.SUBSCRIPTION
  );

  const [getGeneralCost, { data: generalCost }] = useLazyQuery(GENERAL_COST, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (subscriptionId)
      getSubscription({
        variables: { where: { id: subscriptionId } },
      });
  }, []);

  useEffect(() => {
    if (
      get(data, "subscription.insurancePlan.id") &&
      get(data, "subscription.project.id")
    ) {
      getGeneralCost({
        variables: {
          where: {
            project: { id: get(data, "subscription.project.id") },
            insurancePlan: { id: get(data, "subscription.insurancePlan.id") },
          },
        },
      });
    }
  }, [data]);

  const telecoms = defaults(keyBy(get(project, "contact.telecoms"), "system"), {
    [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
  });

  const next = ({ contact, contract }) => {
    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId
          ),
          "createdDate"
        )
      ),
      "status"
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      }
    );
    const mappedTelecoms = map(get(contact, "telecoms"), (item, key) =>
      defaults(item, { id: cuid(), system: key })
    );

    setSuccessModalVisible(true);
    setTimeout(() => {
      setSuccessModalVisible(false);
      onNext({
        payload: {
          contact: {
            ...contact,
            telecoms: mappedTelecoms,
          },
          contract,
        },
        onCompleted: () => {
          generateQuoteDocuments({
            variables: {
              data: {
                project: { id: project.id },
                insurancePlans: [
                  { id: get(data, "subscription.insurancePlan.id") },
                ],
              },
            },
            onCompleted: () => {
              const source = localStorage.getItem("source");
              addLink({
                variables: {
                  data: {
                    url: get(
                      data,
                      "subscription.insurancePlan.config.generatedUrl",
                      `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`
                    ),
                    project: { id: project.id },
                    subscription: { id: subscriptionId },
                    fields: {
                      process: { id: processId },
                      status: { id: get(possibleStatus, "nextStatus") },
                    },
                  },
                },
                onCompleted: ({ addLink }) => {
                  const mappedTelecoms = map(
                    get(contact, "telecoms"),
                    (item, key) => defaults(item, { id: cuid(), system: key })
                  );
                  const telecomsMap = keyBy(mappedTelecoms, "system");

                  const email = get(telecomsMap, "EMAIL.value");
                  const firstName = get(contact, "user.firstname");
                  const lastName = get(contact, "user.lastname");

                  if (!email || !firstName || !lastName) {
                    notification.error({
                      message: "Impossible d'envoyer le webhook",
                      description: `Données manquantes : ${!firstName ? "prénom, " : ""
                        }${!lastName ? "nom, " : ""}${!email ? "email" : ""}`,
                    });
                    return;
                  }

                  const generatedUrl = get(
                    data,
                    "subscription.insurancePlan.config.generatedUrl",
                    `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}?l=${addLink.id}`
                  );

                  sendPersonalizedQuote({
                    variables: {
                      notification: {
                        recipient: email,
                        externalId: v4(),
                        language: "FR",
                        data: {
                          lastName: lastName,
                          firstName: firstName,
                          products: [
                            {
                              label: get(
                                data,
                                "subscription.insurancePlan.name"
                              ),
                              price:
                                get(generalCost, "generalCost.cost") !==
                                  undefined
                                  ? `${get(
                                    generalCost,
                                    "generalCost.cost"
                                  )}€/mois`
                                  : "",
                              url: get(
                                data,
                                "subscription.insurancePlan.config.generatedUrl",
                                `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}?l=${addLink.id}`
                              ),
                            },
                          ],
                          agentFirstName: get(
                            project,
                            "author.firstname",
                            "Camille"
                          ),
                          agentEmail: get(
                            project,
                            "author.email",
                            "camille@mutuelle-familiale.fr"
                          ),
                        },
                      },
                    },
                    onCompleted: () => {
                      notification.open({
                        message: "Devis envoyé",
                        duration: 5,
                        icon: (
                          <IoCheckmarkCircleOutline color={COLORS.C_SUCCESS} />
                        ),
                      });
                    },
                    onError: (err) => {
                      notification.error({
                        message: "Erreur webhook Engage",
                        description: err.message,
                      });
                    },
                  });
                  // sendQuoteMutation({
                  //   variables: {
                  //     data: {
                  //       link: addLink?.id,
                  //       project: { id: project.id },
                  //       insurancePlans: [
                  //         { id: get(data, "subscription.insurancePlan.id") },
                  //       ],
                  //     },
                  //   },
                  //   onCompleted: () =>
                  //     notification.open({
                  //       message: "Devis envoyé",
                  //       duration: 5,
                  //       icon: (
                  //         <IoCheckmarkCircleOutline color={COLORS.C_SUCCESS} />
                  //       ),
                  //     }),
                  // });
                },
              });
            },
          });
        },
      });
    }, 2000);
  };

  const initialValues = {
    contact: {
      ...get(project, "contact", {}),
      telecoms,
      acceptDigital: get(project, "contact.acceptDigital", false),
      acceptContact: get(project, "contact.acceptContact", false),
      fields: {
        ...get(project, "contact.fields", {}),
      },
    },
    contract: get(project, "contract"),
  };

  return (
    <>
      <Modal
        open={successModalVisible}
        footer={null}
        centered
        closable={false}
        className="lmf__modal"
        width={400}
      >
        <div className="lmf__modal--content lmf__modal--center">
          <EmailGreen />
          <h3 className="message">Un devis vous a été envoyé.</h3>
        </div>
      </Modal>
      <Template
        onBack={onBack}
        progress={currentStatus.progress}
        tracer={tracer}
        subscriptionId={subscriptionId}
        project={project}
        HeaderComponent={{
          subscription: get(data, "subscription"),
          tracer,
          project,
        }}
        View={
          <View
            onNext={next}
            initialValues={initialValues}
            fields={get(project, "fields", {})}
            locked={project.locked}
            disabled={project?.locked}
            onBack={onBack}
          />
        }
      />
    </>
  );
};

export default AdherentDetails;
